/**
 * Applicant Status Page
 *
 * DRR-241: Rule-driven hiring pipeline architecture
 *
 * Shows applicants progressing through the Universal Applicant Pipeline (0-0-8).
 * Displays current state, screening progress, and available transitions with guard status.
 *
 * Route: /ferp-core/hiring/applicants
 */

import React from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Card,
  Alert,
  Loader,
  Table,
  Tooltip,
  ActionIcon,
  Breadcrumbs,
  Anchor,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconClock,
  IconRefresh,
  IconUser,
  IconShieldCheck,
  IconShieldX,
  IconEye,
} from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { useListInstancesByWorkflowQuery } from '@/generated/graphql';
import { useTenantId } from '@/auth';

// ============================================================================
// Types
// ============================================================================

interface AvailableTransition {
  event: string;
  toState: string;
  displayName: string;
  guard: string | null;
  guardPassed: boolean;
}

interface ApplicantInstance {
  id: string;
  instanceName?: string | null;
  currentState: string;
  status: string;
  subjectActorId?: string | null;
  subjectName?: string | null;
  availableTransitions?: AvailableTransition[];
}

// ============================================================================
// Helpers
// ============================================================================

const STATE_COLORS: Record<string, string> = {
  prospect: 'gray',
  applicant: 'blue',
  screening: 'yellow',
  hr_review: 'orange',
  offer_extended: 'cyan',
  hired: 'green',
  withdrawn: 'gray',
  rejected: 'red',
  declined: 'pink',
};

const STATE_LABELS: Record<string, string> = {
  prospect: 'Prospect',
  applicant: 'Applied',
  screening: 'Screening',
  hr_review: 'HR Review',
  offer_extended: 'Offer Extended',
  hired: 'Hired',
  withdrawn: 'Withdrawn',
  rejected: 'Rejected',
  declined: 'Declined',
};

function getScreeningProgress(transitions: AvailableTransition[] | undefined): {
  complete: boolean;
  passed: boolean;
  hasFlagged: boolean;
  hasCritical: boolean;
} {
  if (!transitions)
    return { complete: false, passed: false, hasFlagged: false, hasCritical: false };

  const screeningTransition = transitions.find((t) =>
    t.guard?.includes('all_screening_complete_and_passed')
  );
  const flaggedTransition = transitions.find((t) => t.guard?.includes('has_flagged_results'));
  const criticalTransition = transitions.find((t) =>
    t.guard?.includes('has_critical_disqualification')
  );

  return {
    complete: screeningTransition?.guardPassed ?? false,
    passed: screeningTransition?.guardPassed ?? false,
    hasFlagged: flaggedTransition?.guardPassed ?? false,
    hasCritical: criticalTransition?.guardPassed ?? false,
  };
}

// ============================================================================
// Components
// ============================================================================

function ScreeningStatusBadge({ transitions }: { transitions?: AvailableTransition[] }) {
  const progress = getScreeningProgress(transitions);

  if (progress.hasCritical) {
    return (
      <Tooltip label="Critical disqualification found">
        <Badge color="red" leftSection={<IconShieldX size={12} />}>
          Disqualified
        </Badge>
      </Tooltip>
    );
  }

  if (progress.hasFlagged) {
    return (
      <Tooltip label="Flagged results require HR review">
        <Badge color="orange" leftSection={<IconAlertTriangle size={12} />}>
          Review Required
        </Badge>
      </Tooltip>
    );
  }

  if (progress.complete && progress.passed) {
    return (
      <Tooltip label="All screening tasks completed and passed">
        <Badge color="green" leftSection={<IconShieldCheck size={12} />}>
          Cleared
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip label="Screening in progress">
      <Badge color="blue" leftSection={<IconClock size={12} />}>
        In Progress
      </Badge>
    </Tooltip>
  );
}

function ApplicantRow({ applicant }: { applicant: ApplicantInstance }) {
  const stateColor = STATE_COLORS[applicant.currentState] ?? 'gray';
  const stateLabel = STATE_LABELS[applicant.currentState] ?? applicant.currentState;
  const isScreeningState = applicant.currentState === 'screening';

  return (
    <Table.Tr>
      <Table.Td>
        <Group gap="xs">
          <IconUser size={16} />
          <Text fw={500}>{applicant.subjectName ?? applicant.instanceName ?? applicant.id}</Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <Badge color={stateColor} variant="light">
          {stateLabel}
        </Badge>
      </Table.Td>
      <Table.Td>
        {isScreeningState ? (
          <ScreeningStatusBadge transitions={applicant.availableTransitions} />
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {applicant.availableTransitions
            ?.filter((t) => t.guardPassed)
            .map((t) => (
              <Tooltip key={t.event} label={`Fire event: ${t.event}`}>
                <Badge size="xs" color="green" variant="outline">
                  {t.displayName}
                </Badge>
              </Tooltip>
            ))}
        </Group>
      </Table.Td>
      <Table.Td>
        <Tooltip label="View applicant details">
          <ActionIcon component={Link} to={`/ferp-core/instances/${applicant.id}`} variant="subtle">
            <IconEye size={16} />
          </ActionIcon>
        </Tooltip>
      </Table.Td>
    </Table.Tr>
  );
}

// ============================================================================
// Main Component
// ============================================================================

const APPLICANT_WORKFLOW_ID = '0-0-8';

export function ApplicantStatusPage() {
  const tenantId = useTenantId() ?? '';

  const { data, loading, error, refetch } = useListInstancesByWorkflowQuery({
    variables: {
      tenantId,
      workflowDefinitionId: APPLICANT_WORKFLOW_ID,
      limit: 100,
    },
    skip: !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const applicants = (data?.listInstancesByWorkflow?.items ?? []) as unknown as ApplicantInstance[];

  const byState = applicants.reduce(
    (acc, a) => {
      acc[a.currentState] = (acc[a.currentState] ?? 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Breadcrumbs>
          <Anchor component={Link} to="/ferp-core">
            FERP Core
          </Anchor>
          <Text>Hiring</Text>
          <Text>Applicants</Text>
        </Breadcrumbs>

        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Applicant Pipeline</Title>
            <Text c="dimmed" size="sm">
              DRR-241: Universal Applicant Pipeline (0-0-8)
            </Text>
          </div>
          <ActionIcon onClick={() => refetch()} variant="subtle" loading={loading}>
            <IconRefresh size={20} />
          </ActionIcon>
        </Group>

        <Group gap="md">
          {Object.entries(byState).map(([state, count]) => (
            <Card key={state} shadow="xs" padding="sm" radius="md" withBorder>
              <Group gap="xs">
                <Badge color={STATE_COLORS[state] ?? 'gray'} variant="light">
                  {STATE_LABELS[state] ?? state}
                </Badge>
                <Text fw={700} size="lg">
                  {count}
                </Text>
              </Group>
            </Card>
          ))}
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertTriangle />} title="Error loading applicants">
            {error.message}
          </Alert>
        )}

        {loading && applicants.length === 0 && (
          <Group justify="center" py="xl">
            <Loader />
            <Text c="dimmed">Loading applicants...</Text>
          </Group>
        )}

        {!loading && applicants.length === 0 && (
          <Alert color="blue" title="No applicants found">
            No applicants are currently in the hiring pipeline.
          </Alert>
        )}

        {applicants.length > 0 && (
          <Card shadow="xs" padding="md" radius="md" withBorder>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Applicant</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Screening</Table.Th>
                  <Table.Th>Available Actions</Table.Th>
                  <Table.Th w={50}>View</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {applicants.map((applicant) => (
                  <ApplicantRow key={applicant.id} applicant={applicant} />
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

export default ApplicantStatusPage;
